import type { SafetyLevel } from './types';

/**
 * Permission bit flags. A role's `permissions` field is the bitwise OR of these.
 * Server owners implicitly have every permission; ADMINISTRATOR grants all flags.
 * Keep values below 2^30 so they stay safe for 32-bit bitwise operations.
 */
export const Permissions = {
  ADMINISTRATOR: 1 << 0,
  MANAGE_SERVER: 1 << 1,
  MANAGE_CHANNELS: 1 << 2,
  MANAGE_ROLES: 1 << 3,
  MANAGE_MESSAGES: 1 << 4,
  KICK_MEMBERS: 1 << 5,
  BAN_MEMBERS: 1 << 6,
  CREATE_INVITES: 1 << 7,
  SEND_MESSAGES: 1 << 8,
  ADD_REACTIONS: 1 << 9,
  ATTACH_FILES: 1 << 10,
  CONNECT: 1 << 11,
  SPEAK: 1 << 12,
  CHANGE_NICKNAME: 1 << 13,
  MANAGE_NICKNAMES: 1 << 14,
  VIEW_CHANNEL: 1 << 15,
  MODERATE_MEMBERS: 1 << 16,
  VIEW_AUDIT_LOG: 1 << 17,
  MENTION_EVERYONE: 1 << 18,
  MANAGE_EVENTS: 1 << 19,
} as const;

export type PermissionName = keyof typeof Permissions;

export const ALL_PERMISSIONS: number = Object.values(Permissions).reduce((acc, flag) => acc | flag, 0);

export const PERMISSION_INFO: ReadonlyArray<{
  key: PermissionName;
  label: string;
  description: string;
  group: 'General' | 'Membership' | 'Text' | 'Voice';
}> = [
  { key: 'ADMINISTRATOR', label: 'Administrator', description: 'Grants every permission and bypasses all checks. Dangerous.', group: 'General' },
  { key: 'MANAGE_SERVER', label: 'Manage Server', description: 'Rename the server, change its icon and description.', group: 'General' },
  { key: 'MANAGE_CHANNELS', label: 'Manage Channels', description: 'Create, edit and delete channels.', group: 'General' },
  { key: 'MANAGE_ROLES', label: 'Manage Roles', description: 'Create and edit roles below their own, assign them, and set channel permissions.', group: 'General' },
  { key: 'MANAGE_EVENTS', label: 'Manage Events', description: 'Create, edit and cancel server events.', group: 'General' },
  { key: 'VIEW_AUDIT_LOG', label: 'View Audit Log', description: 'See who changed what in the server, and every moderation action.', group: 'General' },
  { key: 'VIEW_CHANNEL', label: 'View Channels', description: 'See channels and read their messages. Channels can override this to become private.', group: 'General' },
  { key: 'CREATE_INVITES', label: 'Create Invites', description: 'Create invite links to this server.', group: 'Membership' },
  { key: 'CHANGE_NICKNAME', label: 'Change Nickname', description: 'Set their own nickname in this server.', group: 'Membership' },
  { key: 'MANAGE_NICKNAMES', label: 'Manage Nicknames', description: "Change other members' nicknames (below their role).", group: 'Membership' },
  { key: 'KICK_MEMBERS', label: 'Kick Members', description: 'Remove members with a lower role from the server.', group: 'Membership' },
  { key: 'BAN_MEMBERS', label: 'Ban Members', description: 'Permanently ban members with a lower role.', group: 'Membership' },
  {
    key: 'MODERATE_MEMBERS',
    label: 'Time Out Members',
    description: 'Stop members with a lower role from chatting, reacting and talking in voice for a while.',
    group: 'Membership',
  },
  { key: 'SEND_MESSAGES', label: 'Send Messages', description: 'Send messages in text channels.', group: 'Text' },
  { key: 'MENTION_EVERYONE', label: 'Mention @everyone', description: 'Notify everyone who can see the channel by writing @everyone.', group: 'Text' },
  { key: 'ADD_REACTIONS', label: 'Add Reactions', description: 'React to messages with emoji.', group: 'Text' },
  { key: 'ATTACH_FILES', label: 'Attach Files', description: 'Upload images and files.', group: 'Text' },
  { key: 'MANAGE_MESSAGES', label: 'Manage Messages', description: "Delete other members' messages.", group: 'Text' },
  { key: 'CONNECT', label: 'Connect', description: 'Join voice channels.', group: 'Voice' },
  { key: 'SPEAK', label: 'Speak', description: 'Transmit audio in voice channels.', group: 'Voice' },
];

export function hasPermission(permissions: number, flag: number): boolean {
  if ((permissions & Permissions.ADMINISTRATOR) === Permissions.ADMINISTRATOR) return true;
  return (permissions & flag) === flag;
}

/**
 * What a timed-out member can't do: anything but read and listen. The owner
 * and Administrators can't be timed out, so they keep everything.
 */
export const TIMEOUT_DENIED =
  Permissions.SEND_MESSAGES |
  Permissions.ADD_REACTIONS |
  Permissions.ATTACH_FILES |
  Permissions.CREATE_INVITES |
  Permissions.CHANGE_NICKNAME |
  Permissions.CONNECT |
  Permissions.SPEAK;

/** A member's permissions while timed out (unchanged for the owner and Administrators). */
export function withTimeout(permissions: number, timedOut: boolean): number {
  if (!timedOut || permissions === ALL_PERMISSIONS) return permissions;
  return permissions & ~TIMEOUT_DENIED;
}

/** Whether a timeout end time is still in the future. */
export function isTimedOut(timeoutUntil: string | Date | null | undefined, now = Date.now()): boolean {
  if (!timeoutUntil) return false;
  return new Date(timeoutUntil).getTime() > now;
}

/**
 * Whether a member still has to accept a server's rules: the server has rules,
 * and they haven't accepted them since they were last asked to. Until then
 * they can read but not chat, like a timeout (the owner and Administrators are exempt).
 */
export function rulesPending(server: { rules: string | null; rulesUpdatedAt: string | Date | null }, acceptedAt: string | Date | null | undefined): boolean {
  if (!server.rules || !server.rulesUpdatedAt) return false;
  return !acceptedAt || new Date(acceptedAt).getTime() < new Date(server.rulesUpdatedAt).getTime();
}

/**
 * What a role members pick themselves may allow: everyday things only, never
 * moderating or managing. (Its real use is unlocking channels.)
 */
export const SELF_ASSIGNABLE_PERMISSIONS =
  Permissions.VIEW_CHANNEL |
  Permissions.SEND_MESSAGES |
  Permissions.ADD_REACTIONS |
  Permissions.ATTACH_FILES |
  Permissions.CONNECT |
  Permissions.SPEAK |
  Permissions.CHANGE_NICKNAME |
  Permissions.CREATE_INVITES;

/** How old an account must be (MEDIUM and up), and how long a member must have been in the server (HIGH). */
export const SAFETY_ACCOUNT_AGE_MS = 5 * 60_000;
export const SAFETY_MEMBER_AGE_MS = 10 * 60_000;

export const SAFETY_LEVELS: ReadonlyArray<{ id: SafetyLevel; label: string; description: string }> = [
  { id: 'NONE', label: 'None', description: 'Anyone who joins can chat straight away.' },
  { id: 'LOW', label: 'Low', description: 'They must have confirmed their email address.' },
  { id: 'MEDIUM', label: 'Medium', description: 'They must also have had their account for at least 5 minutes.' },
  { id: 'HIGH', label: 'High', description: 'They must also have been in this server for at least 10 minutes.' },
];

/** Why a server's safety level still holds a member back, and until when (null: until they confirm their email). */
export interface SafetyHold {
  reason: 'email' | 'wait';
  until: number | null;
}

/**
 * Whether a server's safety level still keeps a member from chatting (and from
 * DMing people they only share this server with). Members with a role are
 * trusted; the caller exempts the owner and Administrators.
 */
export function safetyHold(
  level: SafetyLevel,
  member: { emailVerified: boolean; accountCreatedAt: string | Date; joinedAt: string | Date; hasRole: boolean },
  now = Date.now(),
): SafetyHold | null {
  if (level === 'NONE' || member.hasRole) return null;
  if (!member.emailVerified) return { reason: 'email', until: null };
  if (level === 'LOW') return null;
  let until = new Date(member.accountCreatedAt).getTime() + SAFETY_ACCOUNT_AGE_MS;
  if (level === 'HIGH') until = Math.max(until, new Date(member.joinedAt).getTime() + SAFETY_MEMBER_AGE_MS);
  return until > now ? { reason: 'wait', until } : null;
}

/** What to tell someone a safety level is holding back. */
export function safetyHoldMessage(hold: SafetyHold, now = Date.now()): string {
  if (hold.reason === 'email') return 'This server asks members to confirm their email address before chatting. Check your inbox for the link.';
  const minutes = Math.max(1, Math.ceil(((hold.until ?? now) - now) / 60_000));
  return `This server asks new members to wait a few minutes before chatting. You can start in ${minutes} minute${minutes === 1 ? '' : 's'}.`;
}

/** Resolves the effective permission set for a member. */
export function computePermissions(opts: { isOwner: boolean; rolePermissions: number }): number {
  if (opts.isOwner) return ALL_PERMISSIONS;
  if ((opts.rolePermissions & Permissions.ADMINISTRATOR) === Permissions.ADMINISTRATOR) return ALL_PERMISSIONS;
  return opts.rolePermissions & ALL_PERMISSIONS;
}

/** The rank used for hierarchy checks. Owners outrank every role. */
export const OWNER_RANK = Number.MAX_SAFE_INTEGER;

export function memberRank(opts: { isOwner: boolean; rolePosition: number }): number {
  return opts.isOwner ? OWNER_RANK : opts.rolePosition;
}

/**
 * An actor may act on (kick/ban/re-role) a target only if they strictly outrank them.
 * Nobody can act on the owner, and nobody acts on themselves through moderation.
 */
export function canActOn(actor: { rank: number; userId: string }, target: { rank: number; userId: string; isOwner: boolean }): boolean {
  if (actor.userId === target.userId) return false;
  if (target.isOwner) return false;
  return actor.rank > target.rank;
}

/**
 * A member's roles combined (the default role plus any others): every role's
 * permissions, and the highest role's position as their place in the hierarchy.
 */
export function combineRoles(roles: ReadonlyArray<{ permissions: number; position: number }>): { permissions: number; position: number } {
  let permissions = 0;
  let position = 0;
  for (const role of roles) {
    permissions |= role.permissions;
    position = Math.max(position, role.position);
  }
  return { permissions, position };
}

/** Whether `held` covers every permission in `granted`: members can't hand out permissions they lack. */
export function canGrantPermissions(held: number, granted: number): boolean {
  return (granted & ALL_PERMISSIONS & ~held) === 0;
}

/** Whether an actor may change a member's roles: their own, or those of members they outrank. */
export function canEditMemberRoles(actor: { rank: number; userId: string }, target: { rank: number; userId: string; isOwner: boolean }): boolean {
  return actor.userId === target.userId || canActOn(actor, target);
}

/**
 * Whether an actor may give out or take away a role. Never the default role
 * (everyone has it); otherwise only roles below the actor's own highest role
 * (the owner can use any). Giving a role also needs its permissions (canGrantPermissions).
 */
export function canAssignRole(actor: { isOwner: boolean; rank: number }, role: { position: number; isDefault: boolean }): boolean {
  if (role.isDefault) return false;
  return actor.isOwner || role.position < actor.rank;
}

/* ------------------------------------------------------------------ */
/* Channel permission overrides                                        */
/* ------------------------------------------------------------------ */

/** Permissions a channel can override per role. */
export const CHANNEL_PERMISSIONS =
  Permissions.VIEW_CHANNEL |
  Permissions.SEND_MESSAGES |
  Permissions.ADD_REACTIONS |
  Permissions.ATTACH_FILES |
  Permissions.MANAGE_MESSAGES |
  Permissions.CONNECT |
  Permissions.SPEAK;

export interface ChannelOverwrite {
  roleId: string;
  allow: number;
  deny: number;
}

/**
 * A member's permissions in one channel. The channel's override for the default
 * role applies first, then the overrides of the member's other roles (allows win
 * over denies between roles). The owner and Administrators are never limited.
 * Without View Channel, a member has no permissions in the channel at all.
 */
export function channelPermissions(opts: {
  isOwner: boolean;
  /** Combined permissions of the member's roles (combineRoles). */
  rolePermissions: number;
  defaultRoleId: string;
  /** The member's roles other than the default role. */
  roleIds: readonly string[];
  overwrites: readonly ChannelOverwrite[];
}): number {
  let perms = computePermissions({ isOwner: opts.isOwner, rolePermissions: opts.rolePermissions });
  if (perms === ALL_PERMISSIONS) return perms;
  const everyone = opts.overwrites.find((o) => o.roleId === opts.defaultRoleId);
  if (everyone) perms = (perms & ~(everyone.deny & CHANNEL_PERMISSIONS)) | (everyone.allow & CHANNEL_PERMISSIONS);
  let allow = 0;
  let deny = 0;
  for (const o of opts.overwrites) {
    if (o.roleId === opts.defaultRoleId || !opts.roleIds.includes(o.roleId)) continue;
    allow |= o.allow;
    deny |= o.deny;
  }
  perms = (perms & ~(deny & CHANNEL_PERMISSIONS)) | (allow & CHANNEL_PERMISSIONS);
  return perms & Permissions.VIEW_CHANNEL ? perms : 0;
}

/** Whether a channel is hidden from members with only the default role (shown with a lock). */
export function isPrivateChannel(overwrites: readonly ChannelOverwrite[], defaultRoleId: string | undefined): boolean {
  return overwrites.some((o) => o.roleId === defaultRoleId && (o.deny & Permissions.VIEW_CHANNEL) !== 0);
}

const MEMBER_PERMISSIONS =
  Permissions.VIEW_CHANNEL |
  Permissions.SEND_MESSAGES |
  Permissions.ADD_REACTIONS |
  Permissions.ATTACH_FILES |
  Permissions.CREATE_INVITES |
  Permissions.CONNECT |
  Permissions.SPEAK |
  Permissions.CHANGE_NICKNAME;

/** Roles created with every new server. Position is the hierarchy rank (higher = more senior). */
export const DEFAULT_ROLES: ReadonlyArray<{
  name: string;
  color: string | null;
  permissions: number;
  position: number;
  isDefault: boolean;
  hoist: boolean;
  mentionable: boolean;
}> = [
  { name: 'Administrator', color: '#f43f5e', permissions: Permissions.ADMINISTRATOR | MEMBER_PERMISSIONS, position: 2, isDefault: false, hoist: true, mentionable: false },
  {
    name: 'Moderator',
    color: '#38bdf8',
    permissions:
      MEMBER_PERMISSIONS |
      Permissions.MANAGE_MESSAGES |
      Permissions.KICK_MEMBERS |
      Permissions.BAN_MEMBERS |
      Permissions.MODERATE_MEMBERS |
      Permissions.VIEW_AUDIT_LOG |
      Permissions.MANAGE_CHANNELS |
      Permissions.MANAGE_EVENTS |
      Permissions.MANAGE_NICKNAMES,
    position: 1,
    isDefault: false,
    hoist: true,
    mentionable: false,
  },
  { name: 'Member', color: null, permissions: MEMBER_PERMISSIONS, position: 0, isDefault: true, hoist: false, mentionable: false },
];
